import { Button, Checkbox, Col, Form, Input, MenuProps, Row, Spin, notification } from 'antd';
import { RuleObject } from 'rc-field-form/lib/interface';
import { useContext, useEffect, useMemo, useState } from 'react';
import { flushSync } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from 'redux/hooks';

import { EXPIRED_NOTIFICATION_KEY, MATCHING, STATUS } from '@common/constants';
import { openNewEstimateSameWindow } from '@common/helpers';
import {
    matchingCheckSkipToken,
    useEquivalentProductMatching,
} from '@common/hooks/useEquivalentProductMatching/useEquivalentProductMatching';
import { useEquivalentProductMatchingSummaryAlerts } from '@common/hooks/useEquivalentProductMatching/useEquivalentProductMatchingSummaryAlerts';
import { useLazyEquivalentProductMatchingSummaryAlerts } from '@common/hooks/useEquivalentProductMatching/useLazyEquivalentProductMatchingSummaryAlerts';
import { handleIntegerNumberInput, validateZipCode } from '@common/utils';
import ConfirmationDialog from '@core/components/confirmation-dialog/ConfirmationDialog';
import {
    ConfirmationDialogWithBanner,
    DialogBanner,
} from '@core/components/confirmation-dialog/ConfirmationDialogWithBanner';
import { useAnalyticsEvents } from '@core/hooks/useAnalyticsEvents';
import useEstimateSaver from '@core/hooks/useEstimateSaver';
import { useExpiredEstimateActions } from '@core/hooks/useExpiredEstimateActions';
import useFetchedEstimate from '@core/hooks/useFetchedEstimate';
import { useRetailerSettings } from '@core/hooks/useRetailerSettings';
import useMaterials from '@ee-api/materials-api/useMaterials';
import { useStores } from '@ee-api/stores-api/useStores';
import useFetchZipcodeLocation from '@ee-api/zipcode-api/useFetchZipcodeLocation';
import { DrawingShapeContext } from '@ee-context';
import { selectZipcode } from '@redux/slices/zipcodeSlice';

import { EstimateInfoCard } from '../components/EstimateInfoCard/EstimateInfoCard';
import { OnlineStores } from './OnlineStores';
import { RetailStores } from './RetailStores';
import LocationOutlinedIcon from '/public/assets/icons/location-outlined.svg';

import './zip-code-stores.scss';

const FormItem = Form.Item;

interface ZipCodeStoresProps {
    onSubmit: (value: string) => void;
    loading: boolean;
}

export function ZipCodeStores(props: ZipCodeStoresProps): JSX.Element {
    const { t } = useTranslation();

    const { stores, isFetchingStores } = useStores();
    const { materials, isFetchingMaterials } = useMaterials();

    const { isEstimateDirty } = useEstimateSaver();
    const [newZipCodeCandidate, setNewZipCodeCandidate] = useState<string>();

    const [form] = Form.useForm();
    const { onSubmit } = props;

    const [isLoading, setIsLoading] = useState(false);

    const zipcode = useAppSelector(selectZipcode);
    const [isZipModalVisible, setIsZipModalVisible] = useState(false);

    const { retailerSettings } = useRetailerSettings();
    const fieldNameForValidation = 'ZipCode';

    const { areThereShapesInTheDrawingArea } = useContext(DrawingShapeContext);

    const { fetchedEstimate } = useFetchedEstimate();

    const { fetchZipcodeLocation } = useFetchZipcodeLocation();

    const { replaceProductsWithEquivalents } = useEquivalentProductMatching();
    const { LoaderOnAlerts, replaceIfNoAlerts } = useLazyEquivalentProductMatchingSummaryAlerts();

    const isEquivalentProductMatchingWithFunctionsEnabled = retailerSettings?.enableMatching === MATCHING.RICARDO;

    const { MessageWithAlerts, userMustCancel } = useEquivalentProductMatchingSummaryAlerts(
        newZipCodeCandidate && isEquivalentProductMatchingWithFunctionsEnabled
            ? { zipCode: newZipCodeCandidate }
            : matchingCheckSkipToken
    );

    const [useCurrentDesign, setUseCurrentDesign] = useState(true);

    const { openToNewEstimate, createCloneEstimate } = useExpiredEstimateActions();
    const { trackCloneEstimate } = useAnalyticsEvents();

    useEffect(() => {
        // when zip code is changed from anywhere else it updates input value
        form.setFieldsValue({
            ZipCode: zipcode,
        });
    }, [form, zipcode]);

    const onZipCodeInput = async (event: React.FormEvent<HTMLInputElement>) => {
        const newZipcode = event.currentTarget.value;
        form.getFieldError(fieldNameForValidation);

        if (newZipcode.length === 5 && newZipcode !== zipcode) {
            await submitZipCode(newZipcode);
        }
    };

    const handleZipCodeSubmission = async (newZipcode: string) => {
        if (!zipcode || newZipcode === zipcode) {
            onSubmit(newZipcode);
            return;
        }

        if (isEquivalentProductMatchingWithFunctionsEnabled) {
            replaceIfNoAlerts({
                targetParameters: { zipCode: newZipcode },
                onAlertsCallback: () => {
                    setNewZipCodeCandidate(newZipcode);
                    showZipModal();
                },
                onNoAlertsCallback: () => onSubmit(newZipcode),
            });
        } else {
            onSubmit(newZipcode);
        }
    };

    const submitZipCode = async (newZipcode: string) => {
        try {
            flushSync(() => setIsLoading(true));
            await form.validateFields();
            const { isError } = await fetchZipcodeLocation(newZipcode);
            if (isError) {
                throw new Error('Invalid zipcode');
            }
            await handleZipCodeSubmission(newZipcode);
        } catch {
            form.setFields([
                {
                    name: fieldNameForValidation,
                    errors: [`Invalid zip code${newZipcode ? ':' : ''} ${newZipcode}`],
                    value: zipcode,
                },
            ]);
        } finally {
            setIsLoading(false);
        }
    };

    const onValidate = (rule: RuleObject, value: string) => {
        return validateZipCode(rule, value, t);
    };

    const showZipModal = () => {
        setIsZipModalVisible(true);
    };

    const onConfirmChangingZipCodeFromModal = async () => {
        if (!newZipCodeCandidate) {
            return;
        }

        if (isEquivalentProductMatchingWithFunctionsEnabled) {
            await replaceProductsWithEquivalents({ zipCode: newZipCodeCandidate });
        }
        onSubmit(form.getFieldValue(fieldNameForValidation));
        setIsZipModalVisible(false);
    };

    const onCancelChangingZipCodeFromModal = () => {
        setIsZipModalVisible(false);
        // set previous zipcode
        form.setFieldsValue({
            [fieldNameForValidation]: zipcode,
        });
    };

    const handleRevisionClick = (code: string) => {
        if (code && code !== fetchedEstimate?.code) {
            openNewEstimateSameWindow(code);
        }
    };

    const revisionsMenuItems: MenuProps['items'] = fetchedEstimate?.revisions?.map((version) => ({
        label: version.estimateNumber,
        key: version.code,
        disabled: version.estimateNumber === fetchedEstimate?.estimateNumber,
        onClick: () => handleRevisionClick(version.code),
    }));

    const onCloneEstimate = () => {
        notification.close(EXPIRED_NOTIFICATION_KEY);
        trackCloneEstimate({ status: fetchedEstimate?.status ?? '' });
        createCloneEstimate();
    };

    const onNewEstimate = () => {
        openToNewEstimate();
    };

    const isOnlineRetailer = retailerSettings?.isOnlineRetailer ?? true;

    const showLoader = zipcode && isFetchingStores;

    /**
     * show buttons if it's a fetched estimate
     * AND there are shapes
     * AND it's either a Lead OR it's closed/expired
     */
    const showNewEstimateButtons = useMemo(
        () =>
            (areThereShapesInTheDrawingArea && fetchedEstimate?.estimateNumber.startsWith('EST-')) ||
            [STATUS.CLOSED, STATUS.EXPIRED].some((s) => fetchedEstimate?.status === s),
        [fetchedEstimate?.estimateNumber, fetchedEstimate?.status, areThereShapesInTheDrawingArea]
    );

    return (
        <>
            <LoaderOnAlerts />
            {!fetchedEstimate?.customerZip ? (
                <Form form={form} layout="vertical" initialValues={{ zipcode }}>
                    <div className="container-submit">
                        <FormItem
                            className="label-input"
                            validateTrigger={false}
                            name="ZipCode"
                            rules={[{ validator: onValidate }]}
                            data-testid="zipcode-search"
                        >
                            <Input.Search
                                className="zipcode-entry"
                                type="text"
                                value={zipcode}
                                maxLength={5}
                                placeholder={t('ADDRESS_INFORMATION.ZIP_CODE_PLACEHOLDER')}
                                onChange={onZipCodeInput}
                                size="large"
                                bordered={false}
                                allowClear={false}
                                onKeyDown={handleIntegerNumberInput}
                                onSearch={submitZipCode}
                                loading={isLoading}
                                enterButton={t('ADDRESS_INFORMATION.SEARCH_BUTTON')}
                            />
                        </FormItem>
                    </div>
                </Form>
            ) : (
                <Row gutter={16} align="middle">
                    <Col>
                        <EstimateInfoCard
                            estimateNumber={fetchedEstimate.estimateNumber}
                            customerInfo={{
                                customerName: fetchedEstimate?.customerName,
                                email: fetchedEstimate?.customerEmail,
                                phone: fetchedEstimate?.customerPhone,
                                alternatePhone: fetchedEstimate?.customerAlternatePhone,
                                address1: fetchedEstimate?.customerAddress1,
                                address2: fetchedEstimate?.customerAddress2,
                                city: fetchedEstimate?.customerCity,
                                state: fetchedEstimate?.customerState,
                                zip: fetchedEstimate?.customerZip,
                            }}
                            isEstimateDirty={isEstimateDirty}
                            revisionsMenuItems={revisionsMenuItems}
                        />
                    </Col>
                    <Col>
                        {showNewEstimateButtons ? (
                            <>
                                <Button
                                    className="CreateNew__button"
                                    type="primary"
                                    onClick={useCurrentDesign ? onCloneEstimate : onNewEstimate}
                                >
                                    {t('ADDRESS_INFORMATION.NEW_ESTIMATE.BUTTON')}
                                </Button>
                                <br />
                                <Checkbox
                                    className="CreateNew__checkbox"
                                    checked={useCurrentDesign}
                                    onChange={() => setUseCurrentDesign(!useCurrentDesign)}
                                >
                                    {t('ADDRESS_INFORMATION.NEW_ESTIMATE.CHECKBOX')}
                                </Checkbox>
                            </>
                        ) : null}
                    </Col>
                </Row>
            )}
            {isEquivalentProductMatchingWithFunctionsEnabled && userMustCancel ? (
                <ConfirmationDialog
                    visible={isZipModalVisible}
                    onCancel={onCancelChangingZipCodeFromModal}
                    title={t('EQUIVALENT_PRODUCT_MATCHING.ZIP_CODE.ALERT_TITLE')}
                    content={<MessageWithAlerts />}
                />
            ) : null}
            {isEquivalentProductMatchingWithFunctionsEnabled && !userMustCancel ? (
                <ConfirmationDialogWithBanner
                    visible={isZipModalVisible}
                    banner={
                        <DialogBanner
                            icon={<LocationOutlinedIcon />}
                            bannerText={t('EQUIVALENT_PRODUCT_MATCHING.ALERTS_TEXT')}
                        />
                    }
                    onCancel={onCancelChangingZipCodeFromModal}
                    onConfirm={onConfirmChangingZipCodeFromModal}
                    title={t('ADDRESS_INFORMATION.CHANGE_ZIP_CODE_INFO')}
                    content={<MessageWithAlerts />}
                />
            ) : null}

            {!isEquivalentProductMatchingWithFunctionsEnabled ? (
                <ConfirmationDialog
                    visible={isZipModalVisible}
                    onCancel={onCancelChangingZipCodeFromModal}
                    onConfirm={onConfirmChangingZipCodeFromModal}
                    icon={<LocationOutlinedIcon />}
                    title={t('ADDRESS_INFORMATION.CHANGE_ZIP_CODE_INFO')}
                    content={t('ADDRESS_INFORMATION.DESCRIPTION_CONFIRMATION_MESSAGE')}
                />
            ) : null}

            {showLoader ? <Spin style={{ fontSize: 24 }} /> : null}

            {isOnlineRetailer ? (
                <OnlineStores
                    isFetching={isFetchingStores || isFetchingMaterials}
                    stores={stores}
                    materials={materials}
                    hidden={!zipcode}
                />
            ) : (
                <RetailStores
                    hidden={!zipcode}
                    stores={stores}
                    confirmStoreChange={!!areThereShapesInTheDrawingArea}
                    isFetching={isFetchingStores}
                />
            )}
        </>
    );
}
